import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogContent,
  DialogActions
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import MessageIcon from "@mui/icons-material/Message";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { feedbackApi } from "../services/api";

const ContactFeedback = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    category: "feedback",
    subject: "",
    message: ""
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resultDialog, setResultDialog] = useState({
    open: false,
    type: "success",
    title: "",
    message: ""
  });

  const categories = [
    { value: "feedback", label: "Feedback" },
    { value: "complaint", label: "Complaint" },
    { value: "dealership_enquiry", label: "Enquiry for dealership" },
    { value: "distributership_enquiry", label: "Enquiry for distributership" },
    { value: "suggestion", label: "Suggestion" },
    { value: "other", label: "Other" }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;

    const formattedValue =
      name === "phone"
        ? value.replace(/\D/g, "").slice(0, 10)
        : value;

    setForm({ ...form, [name]: formattedValue });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!form.name.trim()) {
      return setError("Name is required");
    }
    if (!form.email.trim()) {
      return setError("Email is required");
    }
    if (!/\S+@\S+\.\S+/.test(form.email)) {
      return setError("Please enter a valid email");
    }
    if (!form.message.trim()) {
      return setError("Message is required");
    }

    setLoading(true);

    try {
      console.log("📤 Submitting feedback:", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone || "",
        category: form.category,
        subject: form.subject.trim(),
        message: form.message.trim()
      });

      const response = await feedbackApi.submit({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone || "",
        category: form.category,
        subject: form.subject.trim(),
        message: form.message.trim()
      });

      console.log("✅ Response:", response.data);

      if (response.data && response.data.success) {
        setResultDialog({
          open: true,
          type: "success",
          title: "Message sent",
          message: "Thank you for your message. Our team will get back to you soon."
        });
        setForm({
          name: "",
          email: "",
          phone: "",
          category: "feedback",
          subject: "",
          message: ""
        });
      } else {
        setResultDialog({
          open: true,
          type: "error",
          title: "Could not send message",
          message: response.data?.message || "Failed to submit feedback"
        });
      }

    } catch (err) {
      console.error("❌ Feedback submission error:", err);
      console.error("Response data:", err.response?.data);
      setResultDialog({
        open: true,
        type: "error",
        title: "Could not send message",
        message:
          err.response?.data?.message ||
          err.message ||
          "Failed to submit feedback. Please try again."
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ py: 6, backgroundColor: "#f9f9f9", minHeight: "80vh" }}>
      <Container maxWidth="md">
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "#C41E3A",
              mb: 1,
              fontSize: "2rem"
            }}
          >
            Contact & Feedback
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#555",
              fontSize: "1rem"
            }}
          >
            We'd love to hear from you! Share your feedback, complaints, or suggestions.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Form Section */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 4, boxShadow: 2 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2, fontSize: "0.95rem" }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  {/* Name */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Full Name *"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your Name"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon sx={{ color: "#FF6B35" }} />
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: "0.95rem"
                        },
                        "& .MuiInputBase-input::placeholder": {
                          fontSize: "0.95rem",
                          opacity: 0.7
                        }
                      }}
                    />
                  </Grid>

                  {/* Email */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email *"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon sx={{ color: "#FF6B35" }} />
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: "0.95rem"
                        },
                        "& .MuiInputBase-input::placeholder": {
                          fontSize: "0.95rem",
                          opacity: 0.7
                        }
                      }}
                    />
                  </Grid>

                  {/* Phone */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone (Optional)"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="10-digit number"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon sx={{ color: "#FF6B35" }} />
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: "0.95rem"
                        },
                        "& .MuiInputBase-input::placeholder": {
                          fontSize: "0.95rem",
                          opacity: 0.7
                        }
                      }}
                    />
                  </Grid>

                  {/* Category */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      label="Category *"
                      name="category"
                      value={form.category}
                      onChange={handleChange}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: "0.95rem"
                        }
                      }}
                    >
                      {categories.map((cat) => (
                        <MenuItem key={cat.value} value={cat.value} sx={{ fontSize: "0.95rem" }}>
                          {cat.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Subject */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Subject (Optional)"
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      placeholder="Brief subject of your message"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: "0.95rem"
                        },
                        "& .MuiInputBase-input::placeholder": {
                          fontSize: "0.95rem",
                          opacity: 0.7
                        }
                      }}
                    />
                  </Grid>

                  {/* Message */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Message *"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Please share your detailed feedback or complaint..."
                      multiline
                      rows={6}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ mt: 1 }}>
                            <MessageIcon sx={{ color: "#FF6B35" }} />
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: "0.95rem"
                        },
                        "& .MuiInputBase-input::placeholder": {
                          fontSize: "0.95rem",
                          opacity: 0.7
                        }
                      }}
                    />
                  </Grid>

                  {/* Submit Button */}
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      disabled={loading}
                      endIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                      sx={{
                        backgroundColor: "#FF6B35",
                        color: "#fff",
                        py: 1.5,
                        fontSize: "1rem",
                        fontWeight: 600,
                        "&:hover": {
                          backgroundColor: "#E55A2B"
                        },
                        "&:disabled": {
                          backgroundColor: "#ccc"
                        }
                      }}
                    >
                      {loading ? "Sending..." : "Send Feedback"}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>

          {/* Info Section */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, boxShadow: 2, backgroundColor: "#FFF0EB" }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#C41E3A",
                  mb: 2,
                  fontSize: "1.1rem"
                }}
              >
                Get In Touch
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "#333",
                    mb: 0.5,
                    fontSize: "0.95rem"
                  }}
                >
                  📍 Address
                </Typography>
                <Typography sx={{ color: "#555", fontSize: "0.9rem" }}>
                  Swadveda Masale
                  <br />
                  India
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "#333",
                    mb: 0.5,
                    fontSize: "0.95rem"
                  }}
                >
                  📞 Contact
                </Typography>
                <Typography sx={{ color: "#555", fontSize: "0.9rem" }}>
                  Email: info@swadveda.com
                  <br />
                  Phone: +91 90******92
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "#333",
                    mb: 0.5,
                    fontSize: "0.95rem"
                  }}
                >
                  ⏰ Response Time
                </Typography>
                <Typography sx={{ color: "#555", fontSize: "0.9rem" }}>
                  We typically respond within 24-48 hours.
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Dialog
        open={resultDialog.open}
        onClose={() => setResultDialog((current) => ({ ...current, open: false }))}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "24px",
            textAlign: "center",
            p: 1
          }
        }}
      >
        <DialogContent sx={{ pt: 4, px: 4 }}>
          <Box
            sx={{
              width: 70,
              height: 70,
              borderRadius: "50%",
              mx: "auto",
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                resultDialog.type === "success"
                  ? "rgba(46,125,50,0.12)"
                  : "rgba(198,40,40,0.12)",
              color:
                resultDialog.type === "success"
                  ? "#2e7d32"
                  : "#c62828"
            }}
          >
            {resultDialog.type === "success" ? (
              <CheckCircleRoundedIcon sx={{ fontSize: 42 }} />
            ) : (
              <ErrorOutlineRoundedIcon sx={{ fontSize: 42 }} />
            )}
          </Box>

          <Typography variant="h5" fontWeight={800} color="#C41E3A" mb={1}>
            {resultDialog.title}
          </Typography>

          <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {resultDialog.message}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 4, pb: 3 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => setResultDialog((current) => ({ ...current, open: false }))}
            sx={{
              backgroundColor: "#FF6B35",
              borderRadius: "14px",
              py: 1.2,
              textTransform: "none",
              fontWeight: 800,
              "&:hover": {
                backgroundColor: "#E55A2B"
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContactFeedback;
